// src/pages/Cart.jsx
import { useEffect, useState } from "react";
import ProductModal from "../components/ProductModal";
import AddMoreItems from "../components/AddMoreItems";
import { Minus, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Cart() {
  const [cartItems, setCartItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  // LOAD ROBOTO
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    document.body.style.fontFamily = "Roboto, sans-serif";
  }, []);

  // LOAD CART
  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(cart);
  }, []);

  // REMOVE ITEM
  const handleRemove = (id, size, color) => {
    const updated = cartItems.filter(
      (item) => !(item.id === id && item.size === size && item.color === color)
    );
    localStorage.setItem("cart", JSON.stringify(updated));
    setCartItems(updated);
  };

  // QUANTITY CHANGE
  const handleQtyChange = (id, size, color, type) => {
    const updated = cartItems.map((item) => {
      if (item.id === id && item.size === size && item.color === color) {
        let qty = type === "inc" ? item.quantity + 1 : item.quantity - 1;
        return { ...item, quantity: qty < 1 ? 1 : qty };
      }
      return item;
    });
    localStorage.setItem("cart", JSON.stringify(updated));
    setCartItems(updated);
  };

  // SUMMARY CALC
  const subtotal = cartItems.reduce(
    (sum, item) => sum + (item.price || 0) * item.quantity,
    0
  );
  const gst = subtotal * 0.05;
  const total = subtotal + gst;

  return (
    <div className="min-h-screen bg-gray-100 pt-24"> {/* ← Add padding for fixed navbar */}
      {/* REMOVED <Navbar /> FROM HERE */}

      <div className="max-w-7xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* LEFT SIDE */}
        <div className="lg:col-span-2 space-y-4">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Your Cart</h1>

          {cartItems.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl shadow">
              <p className="text-2xl text-gray-600 mb-6">Your cart is empty</p>
              <button
                onClick={() => navigate("/collections")}
                className="px-10 py-4 bg-gray-800 text-white rounded-xl font-bold hover:bg-gray-900"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            cartItems.map((item, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl p-4 shadow flex flex-col md:flex-row items-center gap-4 border"
              >
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-20 h-20 object-contain rounded-lg shadow"
                  onError={(e) => {
                    e.target.src = "https://placehold.co/80x80/cccccc/999999?text=No+Image";
                  }}
                />

                <div className="flex-1 space-y-1 text-sm">
                  <p className="font-bold text-gray-800 text-lg">{item.name}</p>
                  <p className="text-gray-600">Size: <strong>{item.size}</strong></p>
                  <p className="text-gray-600">Color: <strong>{item.color}</strong></p>

                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => handleQtyChange(item.id, item.size, item.color, "dec")}
                      className="p-1.5 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-lg font-semibold">{item.quantity}</span>
                    <button
                      onClick={() => handleQtyChange(item.id, item.size, item.color, "inc")}
                      className="p-1.5 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <p className="font-bold text-gray-800 mt-1">
                    ₹{(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>

                <button
                  onClick={() => handleRemove(item.id, item.size, item.color)}
                  className="text-red-600 text-sm font-bold hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>

        {/* RIGHT — STICKY SUMMARY */}
        {cartItems.length > 0 && (
          <div className="sticky top-24 h-fit">
            <div className="bg-white p-6 rounded-xl shadow-xl border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Order Summary</h2>
              <div className="space-y-3 text-base">
                <div className="flex justify-between">
                  <span className="text-gray-600">Items Total</span>
                  <span className="font-semibold">₹{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">GST (5%)</span>
                  <span className="font-semibold">₹{gst.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivery</span>
                  <span className="text-green-600 font-semibold">FREE</span>
                </div>
                <hr />
                <div className="flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span>₹{total.toFixed(2)}</span>
                </div>
              </div>
              <button
                onClick={() => navigate("/address", { state: { total, cartItems } })}
                className="w-full mt-5 bg-gray-800 text-white py-3 rounded-xl font-bold hover:bg-gray-900"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        )}
      </div>

      <AddMoreItems />

      {isModalOpen && selectedProduct && (
        <ProductModal product={selectedProduct} onClose={() => setIsModalOpen(false)} />
      )}
    </div>
  );
}